import React, { useState } from 'react';

import { Building, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';

import useGetRelativityWorkspaces from '@/api/queries/useGetRelativityWorkspaces';
import { RelativityWorkspaceType } from '@/api/types/backend-dto-types';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/new-button';

interface RelativityWorkspacesModalProps {
  onSelectWorkspace: (workspace: RelativityWorkspaceType) => void;
  onClose: () => void;
}

interface WorkspaceItemProps {
  workspace: RelativityWorkspaceType;
  isSelected: boolean;
  onSelect: (workspace: RelativityWorkspaceType) => void;
}

const WorkspaceItem: React.FC<WorkspaceItemProps> = ({ workspace, isSelected, onSelect }) => {
  return (
    <div
      className={`group cursor-pointer rounded-lg border p-4 transition-all duration-200 hover:shadow-sm ${
        isSelected
          ? 'border-brandSecondary bg-brandSecondary/10 ring-brandSecondary ring-1 shadow-sm'
          : 'hover:border-brandSecondary/30 hover:bg-brandSecondary/5 border-gray-200'
      }`}
      onClick={() => onSelect(workspace)}
    >
      <div className="flex min-w-0 flex-1 items-center space-x-3">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
            isSelected ? 'bg-brandSecondary/20' : 'group-hover:bg-brandSecondary/15 bg-gray-100'
          }`}
        >
          <Building
            className={`h-5 w-5 ${
              isSelected ? 'text-brandSecondary' : 'group-hover:text-brandSecondary text-gray-600'
            }`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={`truncate text-sm font-medium ${
              isSelected ? 'text-brandPrimary' : 'group-hover:text-brandPrimary text-gray-900'
            }`}
            title={workspace.name}
          >
            {workspace.name}
          </h3>
          <p className="truncate text-sm text-gray-500" title={`ID: ${workspace.artifactId}`}>
            ID: {workspace.artifactId}
          </p>
        </div>
      </div>
    </div>
  );
};

const RelativityWorkspacesModal: React.FC<RelativityWorkspacesModalProps> = ({ onSelectWorkspace }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState<RelativityWorkspaceType | null>(null);
  const pageSize = 5;

  // Note: Search is immediate for better UX, but could be debounced if needed

  const {
    data: workspacesData,
    isLoading,
    error,
  } = useGetRelativityWorkspaces({
    page: currentPage,
    pageSize,
    search: searchQuery,
  });

  const handleSelectWorkspace = (workspace: RelativityWorkspaceType) => {
    setSelectedWorkspace(workspace);
  };

  const handleConfirmSelection = () => {
    if (selectedWorkspace) {
      onSelectWorkspace(selectedWorkspace);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
    setSelectedWorkspace(null); // Clear selection when searching
  };

  const totalPages = workspacesData?.totalPages || 0;
  const canPrevious = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <div className="bg-white px-6 py-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Select Relativity Workspace</h2>
        <p className="mt-1 text-sm text-gray-600">Choose a workspace to import documents from Relativity.</p>
      </div>

      {/* Search and Pagination */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Inline Pagination */}
        {workspacesData && totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!canPrevious}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-sm text-gray-600">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!canNext}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="min-h-[320px]">
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
              <p className="mt-2 text-sm text-gray-600">Loading workspaces...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Failed to load workspaces</h3>
                <p className="mt-1 text-sm text-red-700">
                  {error instanceof Error ? error.message : 'An unknown error occurred'}
                </p>
              </div>
            </div>
          </div>
        )}

        {workspacesData && workspacesData.workspaces.length === 0 && !isLoading && (
          <div className="py-20 text-center">
            <Building className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchQuery ? 'No matching workspaces' : 'No workspaces found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'Try adjusting your search terms.' : "You don't have access to any Relativity workspaces."}
            </p>
          </div>
        )}

        {workspacesData && workspacesData.workspaces.length > 0 && (
          <div className="space-y-3">
            {workspacesData.workspaces.map((workspace) => (
              <WorkspaceItem
                key={workspace.artifactId}
                workspace={workspace}
                isSelected={selectedWorkspace?.artifactId === workspace.artifactId}
                onSelect={handleSelectWorkspace}
              />
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
        <div className="flex items-center text-sm text-gray-500">
          {selectedWorkspace && <span className="font-medium text-gray-700">Selected: {selectedWorkspace.name}</span>}
        </div>
        <div className="flex justify-end">
          {selectedWorkspace && (
            <Button variant="outline" onClick={handleConfirmSelection}>
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelativityWorkspacesModal;
